import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { execute } from '../graphql/execute';
import type { CreateSavedViewInput, SavedViewsQuery, UpdateSavedViewInput } from '../graphql/graphql';
import {
  createSavedViewMutation,
  deleteSavedViewMutation,
  setInitialSavedViewMutation,
  updateSavedViewMutation,
} from '../mutations/savedView.mutation';
import { savedViewsQuery } from '../queries/savedView.query';

export type SavedViewListItem = SavedViewsQuery['savedViews'][number];

export const savedViewsQueryKey = (projectId: string) => ['savedViews', projectId];

export const useSavedViews = (projectId: string) =>
  useQuery({
    queryKey: savedViewsQueryKey(projectId),
    queryFn: () => execute(savedViewsQuery, { projectId }),
  });

export const useSavedViewMutations = (projectId: string) => {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: savedViewsQueryKey(projectId) });

  const create = useMutation({
    mutationFn: (input: CreateSavedViewInput) => execute(createSavedViewMutation, { input }),
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: (input: UpdateSavedViewInput) => execute(updateSavedViewMutation, { input }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => execute(deleteSavedViewMutation, { id }),
    onSuccess: invalidate,
  });

  const setInitial = useMutation({
    mutationFn: ({ id, initial }: { id: string; initial: boolean }) =>
      execute(setInitialSavedViewMutation, { id, initial }),
    onSuccess: invalidate,
  });

  return { create, update, remove, setInitial };
};
